import { useState, useMemo } from "react";
import { PRICING_TIERS, PRICING_FEATURES, APP_URLS } from "../data/mock";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Check, ArrowRight, Zap, Users, Mail } from "lucide-react";

export const PricingSection = ({ onOrderClick, onInfoClick }) => {
  const [sliderValue, setSliderValue] = useState([2]);

  const currentTier = useMemo(() => {
    const idx = Math.min(sliderValue[0], PRICING_TIERS.length - 1);
    return PRICING_TIERS[idx];
  }, [sliderValue]);

  const isIndividual = currentTier.price === null;

  return (
    <section id="pricing" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-[#F4F7FC]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0049B3]/10 to-transparent" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FFC72C]/10 border border-[#FFC72C]/20 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-[#E5A800]" />
            <span className="text-sm font-semibold text-[#B8860B]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Jednoduchý ceník — cena dle počtu dětí
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F1B3D]"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: '1.25' }}
          >
            Kolik dětí má vaše
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFC72C] to-[#E5A800]"> mateřská škola?</span>
          </h2>
          <p className="mt-6 text-lg text-[#4A5B7A] leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Posuňte posuvník a zjistěte cenu pro vaši MŠ. Plný přístup po celý rok — laďte a upravujte jak potřebujete.
          </p>
        </div>

        {/* Pricing card with slider */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-[2rem] border border-[#E8EDF5] shadow-2xl shadow-blue-900/8 overflow-hidden">
            {/* Top section - slider + price */}
            <div className="p-8 lg:p-12">
              {/* Current tier label */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-[#0049B3]/8 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#0049B3]" />
                </div>
                <span
                  className="text-xl font-bold text-[#0F1B3D]"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {currentTier.label}
                </span>
              </div>

              {/* Slider */}
              <div className="mb-10 px-2">
                <Slider
                  value={sliderValue}
                  onValueChange={setSliderValue}
                  min={0}
                  max={PRICING_TIERS.length - 1}
                  step={1}
                  className="pricing-slider"
                />
                {/* Tier labels under slider */}
                <div className="flex justify-between mt-4 px-1">
                  {PRICING_TIERS.map((tier, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSliderValue([idx])}
                      className={`text-xs font-medium transition-colors duration-300 ${
                        sliderValue[0] === idx ? 'text-[#0049B3] font-bold' : 'text-[#9CA3AF] hover:text-[#6B7A99]'
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {tier.max <= 140 ? `${tier.min}–${tier.max}` : '140+'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price display */}
              <div className="text-center">
                {isIndividual ? (
                  <div>
                    <p
                      className="text-3xl lg:text-4xl font-extrabold text-[#0049B3]"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      Individuální nabídka
                    </p>
                    <p className="mt-3 text-[#4A5B7A]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Kontaktujte nás pro cenovou nabídku na míru vaší MŠ.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline justify-center gap-2">
                      <span
                        className="text-5xl lg:text-7xl font-extrabold text-[#0F1B3D]"
                        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      >
                        {currentTier.price}
                      </span>
                      <span className="text-2xl font-bold text-[#0F1B3D]/60">Kč</span>
                    </div>
                    <p className="mt-2 text-sm text-[#6B7A99]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      za 1 mateřskou školu / rok
                    </p>
                  </div>
                )}
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                {isIndividual ? (
                  <Button
                    size="lg"
                    className="bg-[#0049B3] hover:bg-[#003380] text-white rounded-2xl font-bold text-base px-10 py-6 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40 transition-all duration-300 group"
                    onClick={() => window.location.href = 'mailto:info@agentsvp.cz?subject=Individuální nabídka Agent ŠVP'}
                  >
                    <Mail className="mr-2 w-5 h-5" />
                    Kontaktovat nás
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      className="bg-[#FFC72C] hover:bg-[#E5B326] text-[#1A1A2E] rounded-2xl font-bold text-base px-10 py-6 shadow-xl shadow-amber-400/25 hover:shadow-amber-400/40 hover:-translate-y-0.5 transition-all duration-300 group"
                      onClick={() => window.open(APP_URLS.register, "_blank", "noopener,noreferrer")}
                    >
                      Objednat nyní
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-2 border-[#0049B3]/15 text-[#0049B3] hover:bg-[#0049B3]/5 hover:border-[#0049B3]/30 rounded-2xl font-semibold text-base px-8 py-6 transition-all duration-300"
                      onClick={onInfoClick}
                    >
                      Chci více informací
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Bottom section - features */}
            <div className="bg-[#F8FAFF] border-t border-[#E8EDF5] p-8 lg:px-12 lg:py-10">
              <h4
                className="text-sm font-bold text-[#6B7A99] uppercase tracking-wider mb-6 text-center"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Co je zahrnuto v ceně
              </h4>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {PRICING_FEATURES.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#0049B3] flex-shrink-0" />
                    <span className="text-sm text-[#3D4F6F]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Info note */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-3 bg-[#FFF9EB] border border-[#FFC72C]/20 rounded-2xl px-6 py-4">
            <Zap className="w-5 h-5 text-[#E5A800] flex-shrink-0" />
            <p className="text-sm text-[#6B5A2E]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Máte více mateřských škol? <span className="font-bold">Kontaktujte nás</span> pro individuální nabídku na <span className="font-semibold text-[#0049B3]">info@agentsvp.cz</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
